import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Checkbox,
  Box,
  Divider,
} from '@chakra-ui/react';
import api from '../../services/api';
import { formatUsdFromCents } from '../../utils/formatCurrency';

const CreditWarningModal = ({
  isOpen,
  onClose,
  operationType,
  creditsNeeded,
  currentBalance,
  onConfirm,
  onOptOut,
  userSubscriptionStatus,
  allowsOverageProceed = false,
  overageCreditsForRequest,
  overagePricePerCreditCents,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const operationLabels = {
    contact_enrichment: 'Contact Enrichment',
    contact_address: 'Address Lookup',
    email_verification: 'Email Verification',
    email_draft: 'Email Drafting',
    custom_field: 'Custom Field Generation',
    export: 'Campaign Export',
    heyreach_export: 'HeyReach Export',
    smartlead_export: 'Smartlead Export',
    file_upload: 'File Upload',
  };

  const handleConfirm = () => {
    if (dontShowAgain && onOptOut) {
      onOptOut(operationType);
    }
    onConfirm();
    onClose();
  };

  const handleCancel = () => {
    if (dontShowAgain && onOptOut) {
      onOptOut(operationType);
    }
    onClose();
  };

  const handleSubscribe = async (productKey) => {
    setIsUpgrading(true);
    try {
      const isNewSubscription = currentTier === 'free';
      
      if (isNewSubscription) {
        const currentUrl = window.location.href;
        const successUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment_success=true`;
        const cancelUrl = `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}payment_canceled=true`;
        
        const response = await api.post('/api/payments/create-subscription-session', {
          product_key: productKey,
          success_url: successUrl,
          cancel_url: cancelUrl
        });
        
        window.location.href = response.data.url;
      } else {
        const response = await api.post('/api/payments/change-subscription', {
          product_key: productKey
        });
        
        const { status, change_type } = response.data;
        
        if (status === 'success') {
          if (change_type === 'upgrade') {
            window.dispatchEvent(new Event('subscriptionUpgraded'));
          } else {
            window.dispatchEvent(new Event('subscriptionDowngraded'));
          }
          
          setTimeout(() => {
            setIsUpgrading(false);
            onClose();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error with subscription:', error);
      setIsUpgrading(false);
    }
  };

  if (creditsNeeded === undefined || currentBalance === undefined) {
    return null;
  }

  const willBeInsufficient = currentBalance < creditsNeeded;
  // Address lookup is billed only for contacts we actually match, so the preview
  // count is an upper bound — frame it as an estimate and explain the matching.
  const isAddressLookup = operationType === 'contact_address';
  const currentTier = userSubscriptionStatus?.subscription || 'free';
  const showOverageConfirm = willBeInsufficient && allowsOverageProceed;
  const balanceAfterRaw = currentBalance - creditsNeeded;
  const balanceAfterDisplay = Math.max(0, balanceAfterRaw);
  const balanceAfterPositive =
    showOverageConfirm || balanceAfterRaw >= 0;

  const upgradeButtonProps = {
    bg: "#fafafa",
    color: "#0a0a0a",
    borderRadius: "full",
    fontWeight: "600",
    fontSize: "13px",
    h: "36px",
    w: "100%",
    _hover: { opacity: 0.85 },
    isLoading: isUpgrading,
    loadingText: "Upgrading...",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="#0a0a0a" color="#fafafa" border="1px solid rgba(255,255,255,0.10)" borderRadius="12px">
        <ModalHeader fontSize="lg" fontWeight="700" letterSpacing="-0.01em" pb={3}>
          Credit Usage Warning
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="#a3a3a3">
              You are about to perform:{' '}
              <Text as="span" color="#fafafa" fontWeight="500">
                {operationLabels[operationType]}
              </Text>
            </Text>

            {isAddressLookup && (
              <Box
                p={4}
                borderRadius="10px"
                bg="rgba(255,255,255,0.02)"
                border="1px solid rgba(255,255,255,0.08)"
              >
                <Text fontSize="sm" color="#a3a3a3" lineHeight="1.55">
                  We match each contact against our identity graph to find their current
                  mailing address. You&apos;re only charged for contacts we successfully
                  match — the amount below is the maximum estimate, so the final charge is
                  often lower.
                </Text>
              </Box>
            )}

            <Box
              p={4}
              borderRadius="10px"
              bg="#171717"
              border="1px solid rgba(255,255,255,0.10)"
            >
              <VStack spacing={2.5} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#a3a3a3">{isAddressLookup ? 'Estimated credits (up to)' : 'Credits Required'}</Text>
                  <Text fontSize="sm" fontWeight="600" color="#fafafa">{creditsNeeded.toLocaleString()}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#a3a3a3">Current Balance</Text>
                  <Text fontSize="sm" fontWeight="600" color="#fafafa">{currentBalance.toLocaleString()}</Text>
                </HStack>
                {showOverageConfirm &&
                  typeof overageCreditsForRequest === 'number' &&
                  overageCreditsForRequest > 0 && (
                    <>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="#a3a3a3">Credits to spend (beyond included)</Text>
                        <Text fontSize="sm" fontWeight="600" color="#fafafa">
                          {overageCreditsForRequest.toLocaleString()}
                        </Text>
                      </HStack>
                      {typeof overagePricePerCreditCents === 'number' &&
                        overagePricePerCreditCents > 0 && (
                          <HStack justify="space-between">
                            <Text fontSize="sm" color="#a3a3a3">Price per overage credit</Text>
                            <Text fontSize="sm" fontWeight="600" color="#fafafa">
                              {formatUsdFromCents(overagePricePerCreditCents)}
                            </Text>
                          </HStack>
                        )}
                    </>
                  )}
                <Divider borderColor="rgba(255,255,255,0.08)" />
                {['starter', 'basic', 'pro'].includes(currentTier) &&
                  userSubscriptionStatus?.credit_overage_spend_cap_cents != null &&
                  userSubscriptionStatus.credit_overage_spend_cap_cents > 0 && (
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="#a3a3a3">Monthly overage cap</Text>
                      <Text fontSize="sm" fontWeight="600" color="#fafafa">
                        {formatUsdFromCents(userSubscriptionStatus.credit_overage_spend_cap_cents)}
                      </Text>
                    </HStack>
                  )}
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#a3a3a3">Balance After</Text>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={balanceAfterPositive ? '#4ade80' : 'rgba(239,68,68,0.85)'}
                  >
                    {balanceAfterDisplay.toLocaleString()}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {showOverageConfirm && (
              <Text fontSize="sm" color="#a3a3a3">
                Included credits are applied first; anything beyond that is billed as overage up to your monthly
                cap and what you have left this period.
              </Text>
            )}

            {willBeInsufficient && !showOverageConfirm && (
              <>
                <Text fontSize="sm" color="#a3a3a3">
                  {currentTier === 'pro'
                    ? "You're on the highest tier. Your credits will renew at the start of your next billing period."
                    : "Upgrade your plan to get more monthly credits:"}
                </Text>

                {currentTier === 'free' && (
                  <Box p={4} borderRadius="10px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                    <VStack spacing={3} align="stretch">
                      <HStack justify="space-between">
                        <Text fontWeight="600" color="#fafafa" fontSize="sm">Starter</Text>
                        <Text fontWeight="500" color="#a3a3a3" fontSize="sm">$200 / month</Text>
                      </HStack>
                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">3,500 credits per month</Text>
                      <Button {...upgradeButtonProps} onClick={() => handleSubscribe('starter_subscription')}>
                        Upgrade to Starter
                      </Button>
                    </VStack>
                  </Box>
                )}

                {(currentTier === 'free' || currentTier === 'starter') && (
                  <Box p={4} borderRadius="10px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                    <VStack spacing={3} align="stretch">
                      <HStack justify="space-between">
                        <Text fontWeight="600" color="#fafafa" fontSize="sm">Basic</Text>
                        <Text fontWeight="500" color="#a3a3a3" fontSize="sm">$800 / month</Text>
                      </HStack>
                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">20,000 credits per month</Text>
                      <Button {...upgradeButtonProps} onClick={() => handleSubscribe('basic_subscription')}>
                        Upgrade to Basic
                      </Button>
                    </VStack>
                  </Box>
                )}

                {(currentTier === 'free' || currentTier === 'starter' || currentTier === 'basic') && (
                  <Box p={4} borderRadius="10px" bg="rgba(255,255,255,0.02)" border="1px solid rgba(255,255,255,0.08)">
                    <VStack spacing={3} align="stretch">
                      <HStack justify="space-between">
                        <Text fontWeight="600" color="#fafafa" fontSize="sm">Pro</Text>
                        <Text fontWeight="500" color="#a3a3a3" fontSize="sm">$3,000 / month</Text>
                      </HStack>
                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">100,000 credits per month</Text>
                      <Button {...upgradeButtonProps} onClick={() => handleSubscribe('pro_subscription')}>
                        Upgrade to Pro
                      </Button>
                    </VStack>
                  </Box>
                )}
              </>
            )}

            {!willBeInsufficient || showOverageConfirm ? (
              <Checkbox
                isChecked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="sm"
                colorScheme="whiteAlpha"
              >
                <Text fontSize="sm" color="#a3a3a3">
                  Don&apos;t show this warning again for {operationLabels[operationType]}
                </Text>
              </Checkbox>
            ) : null}
          </VStack>
        </ModalBody>

        <ModalFooter pt={2}>
          {willBeInsufficient && !showOverageConfirm ? (
            <Button
              variant="ghost"
              color="rgba(255,255,255,0.5)"
              borderRadius="full"
              fontSize="sm"
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          ) : (
            <HStack spacing={3}>
              <Button
                variant="ghost"
                color="rgba(255,255,255,0.5)"
                borderRadius="full"
                fontSize="sm"
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)' }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                bg="#fafafa"
                color="#0a0a0a"
                borderRadius="full"
                fontWeight="600"
                fontSize="13px"
                h="36px"
                px={5}
                _hover={{ opacity: 0.85 }}
                onClick={handleConfirm}
              >
                Confirm &amp; Proceed
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreditWarningModal;
